// src/services/adminUserService.ts
// Service for user management API calls

import apiClient from './apiClient';
import {
  UsersResponse,
  UserStatsResponse,
  UserResponse,
  User,
  UserStats,
  UserFilters,
} from '../types/user';

class AdminUserService {
  /**
   * Get all users with pagination and filters
   */
  async getUsers(params: {
    page?: number;
    limit?: number;
  } & Partial<UserFilters>): Promise<UsersResponse> {
    try {
      const response = await apiClient.get<UsersResponse>('/admin/users', {
        params,
      });
      return response.data;
    } catch (error: any) {
      console.error('Get users error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch users'
      );
    }
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<UserStats> {
    try {
      const response = await apiClient.get<UserStatsResponse>(
        '/admin/users/stats'
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Get user stats error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch user statistics'
      );
    }
  }

  /**
   * Get single user by ID
   */
  async getUserById(userId: string): Promise<User> {
    try {
      const response = await apiClient.get<UserResponse>(
        `/admin/users/${userId}`
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Get user error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch user'
      );
    }
  }

  /**
   * Update user
   */
  async updateUser(
    userId: string,
    data: Partial<User>
  ): Promise<User> {
    try {
      const response = await apiClient.put<UserResponse>(
        `/admin/users/${userId}`,
        data
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Update user error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to update user'
      );
    }
  }

  /**
   * Suspend user
   */
  async suspendUser(userId: string, reason: string): Promise<User> {
    try {
      const response = await apiClient.post<UserResponse>(
        `/admin/users/${userId}/suspend`,
        { reason }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Suspend user error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to suspend user'
      );
    }
  }

  /**
   * Activate user
   */
  async activateUser(userId: string): Promise<User> {
    try {
      const response = await apiClient.post<UserResponse>(
        `/admin/users/${userId}/activate`
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Activate user error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to activate user'
      );
    }
  }

  /**
   * Ban user
   */
  async banUser(userId: string, reason: string): Promise<User> {
    try {
      const response = await apiClient.post<UserResponse>(
        `/admin/users/${userId}/ban`,
        { reason }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Ban user error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to ban user'
      );
    }
  }

  /**
   * Verify user
   */
  async verifyUser(userId: string): Promise<User> {
    try {
      const response = await apiClient.post<UserResponse>(
        `/admin/users/${userId}/verify`
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Verify user error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to verify user'
      );
    }
  }

  /**
   * Delete user
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      await apiClient.delete(`/admin/users/${userId}`);
    } catch (error: any) {
      console.error('Delete user error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to delete user'
      );
    }
  }

  /**
   * Bulk suspend users
   */
  async bulkSuspend(userIds: string[], reason: string): Promise<void> {
    try {
      await apiClient.post('/admin/users/bulk/suspend', {
        userIds,
        reason,
      });
    } catch (error: any) {
      console.error('Bulk suspend error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to suspend users'
      );
    }
  }

  /**
   * Bulk activate users
   */
  async bulkActivate(userIds: string[]): Promise<void> {
    try {
      await apiClient.post('/admin/users/bulk/activate', {
        userIds,
      });
    } catch (error: any) {
      console.error('Bulk activate error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to activate users'
      );
    }
  }

  /**
   * Export users to CSV
   */
  async exportUsers(filters: Partial<UserFilters>): Promise<void> {
    try {
      const response = await apiClient.get('/admin/users/export', {
        params: filters,
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Export users error:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to export users'
      );
    }
  }
}

export default new AdminUserService();